import type { ColumnDef } from '@tanstack/react-table'
import type { IPoiCategory } from '@/shared/api/types/poi-types'
import { useNavigate } from '@tanstack/react-router'
import { Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDeletePoiCategoryMutation, usePoiCategoriesQuery } from '@/entities/poi'
import { useTenantLanguages } from '@/entities/tenant'
import { TenantSearchBar } from '@/features/tenant/tenant-search-bar'
import { useLanguageLabel } from '@/shared/hooks/use-language-label'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { DataTable, DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { resolveCategoryCode } from '@/shared/utils/poi-category-utils'

interface PoiCategoriesConfigProps {
  tenantId: string
}

const ALL_LANGUAGES_VALUE = '__all__'

interface PoiCategoryActionsCellProps {
  category: IPoiCategory
  deletePending: boolean
  onRequestDelete: (category: IPoiCategory) => void
}

function PoiCategoryActionsCell({
  category,
  deletePending,
  onRequestDelete,
}: PoiCategoryActionsCellProps) {
  const { t } = useTranslation()
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Button
        variant="destructive"
        size="sm"
        disabled={deletePending}
        onClick={() => onRequestDelete(category)}
      >
        <Trash2 size={14} className="mr-1.5" />
        {t('common.delete')}
      </Button>
    </div>
  )
}

export function PoiCategoriesConfig({ tenantId }: PoiCategoriesConfigProps) {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const { activeTenantId = '', setActiveTenantId } = useAdminScope()
  const [deletingCategory, setDeletingCategory] = useState<IPoiCategory | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryLanguageCode, setCategoryLanguageCode] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 10

  const deleteMutation = useDeletePoiCategoryMutation()
  const tenantLanguagesQuery = useTenantLanguages(tenantId)
  const tenantLanguageItems = tenantLanguagesQuery.items
  const tenantDefaultLanguage = useMemo(
    () => tenantLanguageItems.find((item) => item.isDefault)?.languageCode ?? '',
    [tenantLanguageItems],
  )
  const languageOptions = useMemo(
    () =>
      tenantLanguageItems
        .filter((item) => item.isActive && item.languageCode?.trim())
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((item) => ({
          label: item.languageName?.trim() || languageLabel(item.languageCode),
          value: item.languageCode.trim(),
        })),
    [tenantLanguageItems, languageLabel],
  )
  const handleLanguageScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 40
      if (
        nearBottom &&
        tenantLanguagesQuery.hasNextPage &&
        !tenantLanguagesQuery.isFetchingNextPage
      ) {
        void tenantLanguagesQuery.fetchNextPage()
      }
    },
    [tenantLanguagesQuery],
  )
  useEffect(() => {
    if (!tenantId || activeTenantId === tenantId) {
      return
    }
    setActiveTenantId(tenantId)
  }, [activeTenantId, setActiveTenantId, tenantId])

  const isCategoryLanguageValid =
    categoryLanguageCode === ALL_LANGUAGES_VALUE ||
    (categoryLanguageCode !== '' &&
      languageOptions.some((item) => item.value === categoryLanguageCode))
  if (!isCategoryLanguageValid) {
    setCategoryLanguageCode(
      tenantDefaultLanguage || (languageOptions[0]?.value ?? ALL_LANGUAGES_VALUE),
    )
  }

  const filterResetKey = `${categoryLanguageCode}::${searchTerm}`
  const [previousFilterResetKey, setPreviousFilterResetKey] = useState(filterResetKey)
  if (previousFilterResetKey !== filterResetKey) {
    setPreviousFilterResetKey(filterResetKey)
    setCurrentPage(1)
  }

  const categoriesQuery = usePoiCategoriesQuery(tenantId, {
    ...(categoryLanguageCode && categoryLanguageCode !== ALL_LANGUAGES_VALUE
      ? { languageCode: categoryLanguageCode }
      : {}),
    ...(searchTerm.trim() ? { keyword: searchTerm.trim() } : {}),
    current: currentPage,
    pageSize: PAGE_SIZE,
  })
  const categories = useMemo(() => categoriesQuery.data?.data ?? [], [categoriesQuery.data?.data])
  const categoriesTotal = categoriesQuery.data?.total ?? 0

  function openEditPage(category: IPoiCategory) {
    void navigate({
      to: scopedAdminPath(`/tenant/${tenantId}/poi-categories/${category.id}/edit`) as never,
    })
  }

  async function handleDeleteConfirm() {
    if (!deletingCategory) {
      return
    }
    try {
      await deleteMutation.mutateAsync(deletingCategory.id)
      showToast.success(t('poiCategories.deleteSuccess'))
      setDeletingCategory(null)
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  const columns = useMemo<ColumnDef<IPoiCategory>[]>(
    () => [
      {
        id: 'icon',
        header: t('poiCategories.icon', { defaultValue: 'Icon' }),
        cell: ({ row }) => (
          <div className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50">
            {row.original.icon ? (
              <img
                src={row.original.icon}
                alt=""
                className="h-7 w-7 rounded-sm object-cover"
                loading="lazy"
              />
            ) : (
              <span className="text-xs text-zinc-400">—</span>
            )}
          </div>
        ),
      },
      {
        id: 'color',
        header: t('poiCategories.color', { defaultValue: 'Color' }),
        cell: ({ row }) => {
          const color = row.original.color
          if (!color) {
            return <span className="text-xs text-zinc-400">—</span>
          }
          return (
            <div className="flex items-center gap-2">
              <span
                className="h-5 w-5 rounded-full border border-zinc-300"
                style={{ backgroundColor: color }}
                aria-hidden="true"
              />
              <span className="font-mono text-xs text-zinc-700">{color.toUpperCase()}</span>
            </div>
          )
        },
      },
      {
        id: 'code',
        header: t('poiCategories.code'),
        cell: ({ row }) => (
          <span className="inline-flex rounded border border-zinc-200 bg-zinc-50 px-2 py-0.5 font-mono text-xs text-zinc-700">
            {resolveCategoryCode(row.original.localizedData, [
              categoryLanguageCode,
              tenantDefaultLanguage,
            ])}
          </span>
        ),
      },
      {
        id: 'scope',
        header: t('poiCategories.scope'),
        cell: () => t('poiCategories.tenantWide'),
      },
      {
        id: 'actions',
        size: 80,
        header: () => null,
        cell: ({ row }) => (
          <PoiCategoryActionsCell
            category={row.original}
            deletePending={deleteMutation.isPending}
            onRequestDelete={setDeletingCategory}
          />
        ),
      },
    ],
    [t, categoryLanguageCode, deleteMutation.isPending, tenantDefaultLanguage],
  )

  return (
    <>
      <DataTableToolbar
        actions={
          <Button
            disabled={!tenantId}
            onClick={() => {
              void navigate({
                to: scopedAdminPath(`/tenant/${tenantId}/poi-categories/create`) as never,
              })
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            {t('poiCategories.create')}
          </Button>
        }
      >
        <DataTableFilterField
          label={t('common.search', { defaultValue: 'Search' })}
          className="min-w-[260px]"
        >
          <TenantSearchBar
            className="w-full max-w-md"
            placeholder={t('poiCategories.searchPlaceholder')}
            onSearch={setSearchTerm}
          />
        </DataTableFilterField>
        <DataTableFilterField
          label={t('common.language', { defaultValue: 'Language' })}
          className="min-w-[180px]"
        >
          <Select
            value={categoryLanguageCode || ALL_LANGUAGES_VALUE}
            onValueChange={setCategoryLanguageCode}
          >
            <SelectTrigger className="h-10">
              <SelectValue placeholder={t('common.language', { defaultValue: 'Language' })} />
            </SelectTrigger>
            <SelectContent onViewportScroll={handleLanguageScroll}>
              <SelectItem value={ALL_LANGUAGES_VALUE}>
                {t('common.all', { defaultValue: 'All' })}
              </SelectItem>
              {languageOptions.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </DataTableFilterField>
      </DataTableToolbar>

      <DataTable
        columns={columns}
        data={categories}
        loading={tenantId ? categoriesQuery.isLoading : false}
        emptyText={t('poiCategories.empty')}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: categoriesTotal,
          onChange: (page) => setCurrentPage(page),
        }}
        onRowClick={openEditPage}
      />

      <ConfirmDialog
        open={Boolean(deletingCategory)}
        title={t('common.delete')}
        description={t('poiCategories.deleteConfirm')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingCategory(null)}
        variant="destructive"
        loading={deleteMutation.isPending}
        loadingLabel={t('common.deleting')}
      />
    </>
  )
}
